import {
  VIEWPORT_DEFAULT_ZOOM,
  VIEWPORT_MAX_ZOOM,
  VIEWPORT_MIN_ZOOM,
} from '../utils/constants.js';

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface ViewportState {
  readonly panX: number;
  readonly panY: number;
  readonly zoom: number;
}

/**
 * 2D viewport transform for the table's "play zone" — the felt, pond, and
 * the three opponent seats. World point W maps to screen point S via
 * `S = W * zoom + pan`. The local hand rack renders outside this transform
 * (fixed screen space) so discard taps never depend on pan/zoom state; see
 * `scenes/SceneRender.ts`.
 *
 * At the identity view (`panX = panY = 0`, `zoom = 1`) world coordinates
 * equal the LayoutManager's screen rects exactly, matching the pre-viewport
 * layout — pan/zoom is purely additive, ported from domino's `Viewport`
 * without its chain-growth auto-fit/tween (the felt is a fixed square, so
 * there is nothing to auto-fit to).
 */
export class Viewport {
  private panX = 0;
  private panY = 0;
  private zoom = VIEWPORT_DEFAULT_ZOOM;
  private userControlled = false;

  getState(): ViewportState {
    return { panX: this.panX, panY: this.panY, zoom: this.zoom };
  }

  isUserControlled(): boolean {
    return this.userControlled;
  }

  markUserControlled(): void {
    this.userControlled = true;
  }

  clearUserControlled(): void {
    this.userControlled = false;
  }

  /** Apply the viewport transform to a 2D context. Caller saves/restores. */
  applyTo(ctx: CanvasRenderingContext2D): void {
    ctx.translate(this.panX, this.panY);
    ctx.scale(this.zoom, this.zoom);
  }

  screenToWorld(p: Point): Point {
    return { x: (p.x - this.panX) / this.zoom, y: (p.y - this.panY) / this.zoom };
  }

  /**
   * Multiplicative zoom around a screen-space anchor. Standard map-style
   * zoom: the world point under the anchor stays under the anchor.
   */
  zoomAt(anchorScreen: Point, factor: number): void {
    const newZoom = clamp(this.zoom * factor, VIEWPORT_MIN_ZOOM, VIEWPORT_MAX_ZOOM);
    if (newZoom === this.zoom) return;
    const k = newZoom / this.zoom;
    this.panX = anchorScreen.x - (anchorScreen.x - this.panX) * k;
    this.panY = anchorScreen.y - (anchorScreen.y - this.panY) * k;
    this.zoom = newZoom;
  }

  panBy(dx: number, dy: number): void {
    this.panX += dx;
    this.panY += dy;
  }

  /** Snap to an exact viewport state (zoom is clamped). */
  resetTo(panX: number, panY: number, zoom: number): void {
    this.panX = panX;
    this.panY = panY;
    this.zoom = clamp(zoom, VIEWPORT_MIN_ZOOM, VIEWPORT_MAX_ZOOM);
  }
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
